import type { HighlightColor, HighlightRange, HighlightResponse, VerseHighlights, VerseHighlightsPayload } from '../types';

export interface HighlightSwatch {
  color: HighlightColor;
  label: string;
  swatch: string;
}

// Class names are spelled out in full so Tailwind's scanner finds them.
export const HIGHLIGHT_SWATCHES: HighlightSwatch[] = [
  { color: 'YELLOW', label: 'Yellow', swatch: 'bg-yellow-300' },
  { color: 'GREEN', label: 'Green', swatch: 'bg-green-400' },
  { color: 'BLUE', label: 'Blue', swatch: 'bg-sky-400' },
  { color: 'PINK', label: 'Pink', swatch: 'bg-pink-400' },
  { color: 'ORANGE', label: 'Orange', swatch: 'bg-orange-400' },
  { color: 'PURPLE', label: 'Purple', swatch: 'bg-violet-400' },
];

// Pale in light mode so dark text stays readable; translucent in dark mode so light text keeps its contrast.
export const HIGHLIGHT_MARK_CLASSES: Record<HighlightColor, string> = {
  YELLOW: 'bg-yellow-200 dark:bg-yellow-400/30',
  GREEN: 'bg-green-200 dark:bg-green-400/30',
  BLUE: 'bg-sky-200 dark:bg-sky-400/30',
  PINK: 'bg-pink-200 dark:bg-pink-400/30',
  ORANGE: 'bg-orange-200 dark:bg-orange-400/30',
  PURPLE: 'bg-violet-200 dark:bg-violet-400/30',
};

/** A selected range [start, end) of one verse's text. A selection across several verses gives one piece per verse. */
export interface SelectionPiece {
  verseNumber: number;
  start: number;
  end: number;
}

export interface TextSegment {
  text: string;
  color: HighlightColor | null;
}

export const chapterKey = (bibleId: number, bookId: number, chapter: number) => `${bibleId}:${bookId}:${chapter}`;

/**
 * Paints [start, end) in `color` over a verse's existing ranges, or erases it when `color` is null. Whatever the new
 * range overlaps is cut back, so the result stays sorted and non-overlapping, which is what the API requires.
 * Neighbouring ranges of the same colour are joined.
 */
export function applyRange(
  ranges: HighlightRange[],
  start: number,
  end: number,
  color: HighlightColor | null,
): HighlightRange[] {
  if (end <= start) return ranges;

  const pieces: HighlightRange[] = [];
  for (const range of ranges) {
    if (range.end <= start || range.start >= end) {
      pieces.push(range);
      continue;
    }
    if (range.start < start) pieces.push({ ...range, end: start });
    if (range.end > end) pieces.push({ ...range, start: end });
  }
  if (color) pieces.push({ start, end, color });
  pieces.sort((a, b) => a.start - b.start);

  const joined: HighlightRange[] = [];
  for (const piece of pieces) {
    const last = joined[joined.length - 1];
    if (last && last.color === piece.color && last.end === piece.start) {
      last.end = piece.end;
    } else {
      joined.push({ ...piece });
    }
  }
  return joined;
}

/** Applies per-verse replacements to a chapter's highlights, dropping verses left with none. */
export function mergeVerseHighlights(current: VerseHighlights, changes: VerseHighlights): VerseHighlights {
  const merged: VerseHighlights = { ...current, ...changes };
  for (const verse of Object.keys(changes)) {
    if (merged[Number(verse)].length === 0) delete merged[Number(verse)];
  }
  return merged;
}

/**
 * Splits a verse's text into plain and highlighted runs. Ranges are clamped to the text, so highlights saved against
 * an older import of the translation cannot break rendering.
 */
export function segmentText(text: string, ranges: HighlightRange[] = []): TextSegment[] {
  const segments: TextSegment[] = [];
  let cursor = 0;
  for (const range of ranges) {
    const start = Math.max(range.start, cursor);
    const end = Math.min(range.end, text.length);
    if (end <= start) continue;
    if (start > cursor) segments.push({ text: text.slice(cursor, start), color: null });
    segments.push({ text: text.slice(start, end), color: range.color });
    cursor = end;
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor), color: null });
  return segments;
}

export function groupByVerse(highlights: HighlightResponse[]): VerseHighlights {
  const byVerse: VerseHighlights = {};
  for (const h of highlights) {
    (byVerse[h.verseNumber] ??= []).push({ start: h.startOffset, end: h.endOffset, color: h.color });
  }
  return byVerse;
}

export function toVersePayload(
  bibleId: number,
  bookId: number,
  chapter: number,
  verseNumber: number,
  ranges: HighlightRange[],
): VerseHighlightsPayload {
  return {
    bibleTranslationId: bibleId,
    canonicalBookId: bookId,
    chapterNumber: chapter,
    verseNumber,
    ranges: ranges.map(r => ({ startOffset: r.start, endOffset: r.end, color: r.color })),
  };
}
